"use client";

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/catalyst/button";
import { db } from "../../../config/firebaseConfig";
import { collection, collectionGroup, doc, getDoc, getDocs } from "firebase/firestore";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-24">
      <img
        src="/document-folders.svg"
        className="h-32 w-32 opacity-80"
        alt="empty"
      />

      <p className="mt-6 text-sm text-dark-text">
        It seems you haven’t created any annexures yet. Click on 'Create Annexure'
        to get started!
      </p>

      <Button color="brand" className="mt-6" onClick={onCreate}>
        Create Annexure
      </Button>
    </div>
  );
}

export default function AnnexureListView() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [annexures, setAnnexures] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [filterDisconnected, setFilterDisconnected] = useState(false);
  const { user } = useSelector((s: RootState) => s.auth);

  useEffect(() => {
    async function load() {
      // Load annexures
      const annexureSnap = await getDocs(collection(db, "annexures", "meta", "data"));
      const annexureList = annexureSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  
      // Load ALL published templates (no indexes needed)
      const versionSnap = await getDocs(collectionGroup(db, "versions"));
      const publishedDocs = versionSnap.docs.filter(d => d.id === "published");
  
      // Build mapping: which template contains which annexure
      const templateMap: Record<string, { id: string; title: string }[]> = {};
  
      for (const tplDoc of publishedDocs) {
        const data = tplDoc.data();
        const annexureIds: string[] = data.annexureIds || [];
  
        // Get template meta (title)
        const templateId = tplDoc.ref.parent?.parent?.id;
        if (!templateId) continue;
  
        const metaRef = doc(db, "templates", "meta", "data", templateId);
        const metaSnap = await getDoc(metaRef);
        const meta = metaSnap.exists() ? metaSnap.data() : {};
        const templateTitle = meta.title || "Untitled Template";
  
        // Map each annexure
        annexureIds.forEach(id => {
          if (!templateMap[id]) templateMap[id] = [];
          templateMap[id].push({
            id: templateId,
            title: templateTitle
          });
        });
      }
  
      // Attach connectedTemplates into each annexure
      const finalList = annexureList.map(a => ({
        ...a,
        connectedTemplates: templateMap[a.id] || []
      }));
  
      setAnnexures(finalList);
      setLoading(false);
    }
  
    load();
  }, []);
  

  if (loading) return <div className="p-8 text-white">Loading...</div>;

  if (annexures.length === 0) {
    return <EmptyState onCreate={() => navigate("/admin/annexures/new")} />;
  }

  // filter data
  const filtered = annexures.filter((a) => {
    if (filterDisconnected && (a.connectedTemplates?.length ?? 0) > 0)
      return false;
    if (
      search &&
      !a.title.toLowerCase().includes(search.toLowerCase())
    )
      return false;
    return true;
  });

  return (
    <div className="mx-8 my-6">
      <div className="mb-8">
        <h1 className="text-xl font-semibold">Annexures</h1>
      </div>

      {/* Search */}
      <div className="flex justify-between items-center">
        <input
          className="w-full max-w-md border rounded-lg px-3 py-2"
          placeholder="Search by annexure name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {(user?.role === "admin" || user?.role === "approver") && (
          <Button
            color="brand"
            onClick={() => navigate("/admin/annexures/new")}
          >
            + Add annexure
          </Button>
        )}
      </div>

      {/* Toggle */}
      <div className="flex items-center gap-2 mt-3">
        <span className="text-sm">
          Only show annexures that are not connected to a report
        </span>

        <button
          type="button"
          onClick={() => setFilterDisconnected(!filterDisconnected)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out 
            ${
              filterDisconnected
                ? "bg-[rgb(var(--color-brand-600))]"
                : "bg-gray-300"
            }
          `}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
              ${filterDisconnected ? "translate-x-5" : "translate-x-0"}
            `}
          />
        </button>
      </div>

      {/* Table */}
      <div className="border rounded-xl mt-6 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-left text-gray-600">
            <tr>
              <th className="px-4 py-2 w-32">Number</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Connected with</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((a) => (
              <tr
                key={a.id}
                className="border-t cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/admin/annexures/${a.id}`)}
              >
                <td className="px-4 py-2">{a.number}</td>
                <td className="px-4 py-2    ">{a.title}</td>

                {/* 🔥 Connected templates as pills (same as detail page) */}
                <td className="px-4 py-2">
  {a.connectedTemplates?.length > 0 ? (
    <div className="flex flex-wrap gap-1">
      {a.connectedTemplates.map((tpl: any, index: number) => (
        <span key={tpl.id} className="truncate max-w-[20ch]">
          <span
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/templates/${tpl.id}?ver=published`);
            }}
            className="text-brand-600 hover:underline cursor-pointer"
          >
            {tpl.title}
          </span>

          {/* comma except last */}
          {index < a.connectedTemplates.length - 1 && <span>, </span>}
        </span>
      ))}
    </div>
  ) : (
    <span className="text-gray-400">--</span>
  )}
</td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination placeholder */}
      <div className="flex justify-center gap-6 mt-6 items-center text-sm text-gray-600">
        <span className="cursor-pointer text-gray-400">← Previous</span>
        <span className="px-3 py-1 bg-gray-200 rounded-lg">1</span>
        <span className="cursor-pointer text-gray-400">Next →</span>
      </div>
    </div>
  );
}
